import { Box, Button, Stack, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Link as RouterLink } from "react-router-dom";

export interface ErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

export default function ErrorFallback({ error, onReset }: ErrorFallbackProps) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8, px: 2 }}>
      <Stack
        spacing={2}
        alignItems="center"
        sx={{ maxWidth: 480, textAlign: "center" }}
      >
        <ErrorOutlineIcon
          aria-hidden="true"
          sx={{ fontSize: 48, color: "error.main" }}
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: "1.5rem" }}>
          Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary">
          An unexpected error stopped this page from loading. You can try the
          page again, refresh, or head back to the dashboard.
        </Typography>
        {import.meta.env.DEV && (
          <Box
            component="details"
            sx={{ width: "100%", textAlign: "left", mt: 2 }}
          >
            <Box
              component="summary"
              sx={{ cursor: "pointer", color: "text.secondary" }}
            >
              Error details (development only)
            </Box>
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 2,
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                fontSize: "0.75rem",
                overflowX: "auto",
              }}
            >
              {error.message}
              {"\n\n"}
              {error.stack}
            </Box>
          </Box>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="contained" onClick={onReset}>
            Try again
          </Button>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Reload page
          </Button>
          <Button component={RouterLink} to="/dashboard" variant="text">
            Go to dashboard
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
